import type { EventSummary, StageLook } from '@rabbitfuel/shared';

/**
 * Hintergrund hinter dem Kamerabild.
 *
 * Liegt als eigene Ebene im `.player`, nicht im Video. Damit gilt sie fuer
 * Zuschauersicht, Regie-Monitor und die Aufnahmeseite gleich -- und weil der
 * RTMP-Ausgang die fertig gesetzte Seite abfilmt, ist sie im YouTube-Stream
 * automatisch mit im Bild.
 *
 * Sichtbar wird sie erst dadurch, dass das Kamerabild `object-fit: contain`
 * hat: ein Hochkant-Handyvideo laesst links und rechts Platz, und genau dort
 * sass vorher Schwarz.
 */
export function PlayerBackdrop({ stage, event }: { stage: StageLook; event: EventSummary | null }) {
  if (stage.backdrop === 'none') return null;

  const url =
    stage.backdrop === 'cover' ? event?.coverUrl : stage.backdrop === 'image' ? event?.backdropUrl : null;
  // Ohne hochgeladenes Bild bleibt es schwarz -- besser als eine leere Flaeche
  // in einer Farbe, die niemand gewaehlt hat.
  if (stage.backdrop !== 'accent' && !url) return null;

  const accent = event?.accent ?? '#E11D2E';

  return (
    <div className="player__backdrop" aria-hidden>
      {url ? (
        // `scale` ueberdeckt die weichen Raender, die das Weichzeichnen am
        // Bildrand erzeugt -- sonst leuchtet dort ein heller Saum auf.
        <img src={url} alt="" style={{ filter: `blur(${stage.blur}px)`, transform: `scale(${1 + stage.blur / 120})` }} />
      ) : (
        <div
          className="player__backdrop-accent"
          style={{
            /*
             * Kraeftig genug, dass der Verlauf das Abdunkeln ueberlebt: er
             * liegt unter derselben schwarzen Ebene wie ein Foto, und mit
             * zarten Alphawerten bliebe davon nichts als Schwarz uebrig.
             */
            background:
              `radial-gradient(125% 95% at 50% 8%, ${accent}b3, transparent 66%), ` +
              `linear-gradient(168deg, #23262c 0%, ${accent}70 55%, #101215 100%)`,
          }}
        />
      )}
      <div className="player__backdrop-dim" style={{ opacity: stage.dim / 100 }} />
    </div>
  );
}

/**
 * Liegt ueberhaupt ein Hintergrund im Bild? „Titelbild" ohne hochgeladenes
 * Titelbild ist keiner -- deshalb reicht der Modus als Auskunft nicht.
 */
export const hasBackdrop = (stage: StageLook, event: EventSummary | null): boolean =>
  stage.backdrop === 'accent' ||
  (stage.backdrop === 'cover' && Boolean(event?.coverUrl)) ||
  (stage.backdrop === 'image' && Boolean(event?.backdropUrl));

/** `data-`Attribute, die das Stylesheet fuer den Hintergrund braucht. */
export function backdropChrome(stage: StageLook, event: EventSummary | null) {
  return { 'data-backdrop': hasBackdrop(stage, event) ? 'on' : 'off', 'data-fit': stage.fit };
}
